import { zIndex } from 'constants/zIndex';
import React from 'react';
import { Box, PopoverBase, Flex } from '@landr/maestro';
import { css, cva, cx } from '@landr/styled-system/css';
import { CoachmarkProps, IconWrapperProps } from './Coachmark.types';

const coachmarkStyles = cva({
    base: {
        width: 'auto',
        minWidth: '360px',
        padding: 'xl',
        margin: 'sm',
    },
    variants: {
        variant: {
            base: {
                color: 'text.high',
                bg: 'surface.high',
            },
            accent: {
                color: 'text.info.inverted',
                bg: 'surface.info.high',
                borderColor: 'border.info',
            },
        },
    },
});

const iconStyles = cva({
    base: {
        borderRadius: 'round',
        width: '32px',
        height: '32px',
        padding: '2xs',
        borderWidth: '2px',
        borderStyle: 'solid',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        shadow: 'xs',
    },
    variants: {
        variant: {
            base: {
                color: 'icon.inverted.high',
                borderColor: 'border.high',
                bg: 'surface.inverted',
            },
            accent: {
                color: 'icon.secondary',
                borderColor: 'border.secondary',
                bg: 'surface.handle',
            },
        },
    },
});

const placementStyles = css({
    '[data-popper-placement^="top"] > &': { marginBottom: 0 },
    '[data-popper-placement^="right"] > &': { marginLeft: 0 },
    '[data-popper-placement^="bottom"] > &': { marginTop: 0 },
    '[data-popper-placement^="left"] > &': { marginRight: 0 },
    '& + [data-popper-arrow]': {
        zIndex: zIndex.popover,
    },
});

const arrowStyles = cva({
    base: {
        _before: {
            content: '""',
        },
    },
    variants: {
        variant: {
            base: {
                _before: {
                    bg: 'surface.high',
                },
            },
            accent: {
                _before: {
                    bg: 'surface.secondary',
                },
            },
        },
    },
});

const createIconWrapperStyles = (variant: CoachmarkProps['variant']) =>
    iconStyles({ variant });

const anchorBaseStyles = css({
    pointerEvents: 'none',
});

const anchorAbsoluteStyles = css({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    maxWidth: '100% !important',
});

const anchorFixedStyles = css({
    position: 'fixed',
    width: 0,
    height: 0,
});

const headerStyles = css({
    alignItems: 'center',
});

export const Coachmark = ({
    variant,
    children,
    ...props
}: CoachmarkProps & { children: React.ReactNode }) => (
    <PopoverBase
        className={`${coachmarkStyles({
            variant,
        })} ${placementStyles} ${arrowStyles({ variant })}`}
        data-color-mode="dark"
        {...props}
    >
        {children}
    </PopoverBase>
);

export const IconWrapper = ({
    variant,
    children,
    ...props
}: IconWrapperProps & { children: React.ReactNode }) => (
    <Box className={createIconWrapperStyles(variant)} {...props}>
        {children}
    </Box>
);

export const Anchor = ({
    children,
    ...props
}: {
    children: React.ReactNode;
}) => (
    <Box className={anchorBaseStyles} {...props}>
        {children}
    </Box>
);

export const AnchorAbsolute = ({
    children,
    ...props
}: {
    children: React.ReactNode;
}) => (
    <Box className={`${anchorBaseStyles} ${anchorAbsoluteStyles}`} {...props}>
        {children}
    </Box>
);

export const AnchorFixed = ({
    children,
    ...props
}: {
    children: React.ReactNode;
}) => (
    <Box className={cx(anchorBaseStyles, anchorFixedStyles)} {...props}>
        {children}
    </Box>
);

export const Header = ({
    children,
    ...props
}: {
    children: React.ReactNode;
}) => (
    <Flex className={headerStyles} {...props}>
        {children}
    </Flex>
);

export const HeaderItem = Flex;
